import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useColorTheme } from '../../context/ColorThemeContext';

// Get API URL from environment variable with fallback
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
const API = `${BACKEND_URL}/api`;

// Hash password using SHA-256 before sending to server
async function hashPassword(password) {
  const encoder = new TextEncoder();
  const data = encoder.encode(password);
  const hashBuffer = await crypto.subtle.digest('SHA-256', data);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
  return hashHex;
}

const LoginModal = ({ isOpen, onClose }) => {
  const { currentColors } = useColorTheme();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [oauthConfig, setOauthConfig] = useState(null);
  const [googleScriptLoaded, setGoogleScriptLoaded] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });

  // Handle Google credential response
  const handleGoogleCredentialResponse = useCallback(async (response) => {
    setIsLoading(true);
    setError('');
    
    try {
      // Check for referral parameter in URL
      const urlParams = new URLSearchParams(window.location.search);
      const referral = urlParams.get('ref') || urlParams.get('referral') || null;
      
      const result = await axios.post(`${API}/auth/google-callback`, {
        credential: response.credential,
        referral: referral
      });
      
      const { session_token } = result.data;
      localStorage.setItem('session_token', session_token);
      window.location.reload();
      
    } catch (err) {
      console.error('Google login error:', err);
      
      if (err.response?.status === 202) {
        setSuccess('🎉 Account created! You\'ve been added to our waitlist. We\'ll notify you once your account is approved.');
        setError('');
      } else if (err.response?.status === 403) {
        setError('Your account is pending approval. Please wait for admin approval.');
      } else {
        setError(err.response?.data?.detail || 'Google sign-in failed. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Fetch OAuth configuration on mount
  useEffect(() => {
    const fetchOAuthConfig = async () => {
      try {
        console.log('Fetching OAuth config from:', `${API}/auth/oauth-config`);
        const response = await axios.get(`${API}/auth/oauth-config`);
        console.log('OAuth config received:', response.data);
        setOauthConfig(response.data);
      } catch (err) {
        console.error('Failed to fetch OAuth config:', err);
        console.error('Error details:', err.response?.status, err.response?.data);
        // Only fall back to Emergent on non-production domains
        const productionDomains = ['adelevate.3stech.ai', 'contentstudiosai.com'];
        if (!productionDomains.includes(window.location.hostname)) {
          setOauthConfig({ provider: 'emergent', auth_url: 'https://auth.emergentagent.com' });
        } else {
          // On production, retry once after a delay
          setTimeout(async () => {
            try {
              const retryResponse = await axios.get(`${API}/auth/oauth-config`);
              console.log('OAuth config retry received:', retryResponse.data);
              setOauthConfig(retryResponse.data);
            } catch (retryErr) {
              console.error('OAuth config retry failed:', retryErr);
              setError('Unable to load sign-in options. Please refresh the page.');
            }
          }, 1000);
        }
      }
    };
    
    if (isOpen) {
      fetchOAuthConfig();
    }
  }, [isOpen]);

  // Load and initialize Google Sign-In for custom OAuth
  useEffect(() => {
    if (!oauthConfig || oauthConfig.provider !== 'custom' || !oauthConfig.google_client_id) {
      return;
    }

    // Load Google Identity Services script
    const loadGoogleScript = () => {
      if (document.getElementById('google-gsi-script')) {
        // Script already exists, just initialize
        if (window.google?.accounts?.id) {
          initializeGoogleSignIn();
        }
        return;
      }

      const script = document.createElement('script');
      script.id = 'google-gsi-script';
      script.src = 'https://accounts.google.com/gsi/client';
      script.async = true;
      script.defer = true;
      script.onload = () => {
        setGoogleScriptLoaded(true);
        initializeGoogleSignIn();
      };
      script.onerror = () => {
        console.error('Failed to load Google Sign-In script');
        setError('Failed to load Google Sign-In. Please try again.');
      };
      document.head.appendChild(script);
    };

    const initializeGoogleSignIn = () => {
      if (window.google?.accounts?.id && oauthConfig?.google_client_id) {
        try {
          window.google.accounts.id.initialize({
            client_id: oauthConfig.google_client_id,
            callback: handleGoogleCredentialResponse,
            auto_select: false,
            cancel_on_tap_outside: true,
            context: 'signin',
            ux_mode: 'popup'
          });
          setGoogleScriptLoaded(true);
          console.log('Google Sign-In initialized successfully');
        } catch (err) {
          console.error('Failed to initialize Google Sign-In:', err);
        }
      }
    };

    loadGoogleScript();
  }, [oauthConfig, handleGoogleCredentialResponse]);

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleEmailLogin = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    try {
      // Check what password format this user needs
      const formatCheck = await axios.get(`${API}/auth/password-format?email=${encodeURIComponent(formData.email.trim())}`);
      const useHashedPassword = formatCheck.data.format === 'hashed';
      
      // Hash password only for users with hashed format
      const passwordToSend = useHashedPassword 
        ? await hashPassword(formData.password)
        : formData.password;
      
      const response = await axios.post(`${API}/auth/login-email`, {
        email: formData.email.trim(),
        password: passwordToSend,
        password_hashed: useHashedPassword
      });
      
      const { session_token } = response.data;
      localStorage.setItem('session_token', session_token);
      window.location.reload();
      
    } catch (err) {
      console.error('Login error details:', err.response || err);
      
      if (err.response?.status === 401) {
        setError('Invalid email or password. Please check your credentials and try again.');
      } else if (err.response?.status === 429) {
        setError('Too many login attempts. Please wait a moment and try again.');
      } else if (err.response?.status === 500) {
        setError('Server error. Please try again in a moment.');
      } else {
        setError(err.response?.data?.detail || 'Login failed. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    if (!formData.email) {
      setError('Please enter your email address');
      return;
    }

    setIsLoading(true);
    setError('');
    
    try {
      await axios.post(`${API}/auth/forgot-password`, { email: formData.email });
      setSuccess('Password reset link sent! Please check your email.');
      setShowForgotPassword(false);
    } catch (err) {
      setError('Failed to send reset email. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleLogin = () => {
    console.log('handleGoogleLogin called, oauthConfig:', oauthConfig);
    
    // Check if we're on a production domain (not preview)
    const productionDomains = ['adelevate.3stech.ai', 'contentstudiosai.com', 'ad-tech-debug.emergent.host'];
    const isProductionDomain = productionDomains.includes(window.location.hostname);
    
    // Use custom OAuth on production domains when configured
    if (oauthConfig?.provider === 'custom' && isProductionDomain && oauthConfig?.google_client_id) {
      console.log('Using CUSTOM OAuth flow (production)');
      initiateGoogleOAuthRedirect();
    } else {
      // Use Emergent Auth for preview/development or when custom not configured
      console.log('Using EMERGENT OAuth flow (preview/dev or custom not configured)');
      setIsLoading(true);
      const redirectUrl = encodeURIComponent(window.location.origin);
      const authUrl = 'https://auth.emergentagent.com';
      window.location.href = `${authUrl}/?redirect=${redirectUrl}&provider=google`;
    }
  };

  const initiateGoogleOAuthRedirect = () => {
    if (!oauthConfig?.google_client_id) {
      setError('Google Sign-In is not configured properly.');
      return;
    }

    setIsLoading(true);
    
    // Check for referral parameter in URL
    const urlParams = new URLSearchParams(window.location.search);
    const referral = urlParams.get('ref') || urlParams.get('referral') || null;
    
    // Build Google OAuth 2.0 authorization URL
    const googleAuthUrl = 'https://accounts.google.com/o/oauth2/v2/auth';
    const params = new URLSearchParams({
      client_id: oauthConfig.google_client_id,
      redirect_uri: window.location.origin + '/auth/google/callback',
      response_type: 'code',
      scope: 'openid email profile',
      access_type: 'offline',
      prompt: 'consent',
      state: btoa(JSON.stringify({ 
        returnUrl: window.location.pathname,
        referral: referral,
        timestamp: Date.now()
      }))
    });

    window.location.href = `${googleAuthUrl}?${params.toString()}`;
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 sm:p-8 w-full max-w-md relative max-h-[90vh] overflow-y-auto">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 text-2xl"
        >
          ×
        </button>
        
        <div className="text-center mb-6">
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white mb-2">
            {showForgotPassword ? 'Reset Password' : 'Welcome Back'}
          </h2>
          <p className="text-gray-600 dark:text-gray-400 text-sm sm:text-base">
            {showForgotPassword ? 'Enter your email to receive a reset link' : 'Sign in to your AdElevate account'}
          </p>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-100 dark:bg-red-900 border border-red-400 text-red-700 dark:text-red-200 rounded-lg text-sm">
            {error}
          </div>
        )}

        {success && (
          <div className="mb-4 p-3 bg-green-100 dark:bg-green-900 border border-green-400 text-green-700 dark:text-green-200 rounded-lg text-sm">
            {success}
          </div>
        )}

        {showForgotPassword ? (
          <form onSubmit={handleForgotPassword} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Email Address
              </label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                required
                style={{ borderColor: 'rgb(209 213 219)' }}
                className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:border-transparent dark:bg-gray-700 dark:text-white text-sm sm:text-base"
                onFocus={(e) => e.target.style.borderColor = currentColors.primary}
                onBlur={(e) => e.target.style.borderColor = 'rgb(209 213 219)'}
                placeholder="Enter your email"
              />
            </div>

            <div className="flex flex-col sm:flex-row space-y-3 sm:space-y-0 sm:space-x-3">
              <button
                type="button"
                onClick={() => {
                  setShowForgotPassword(false);
                  setError('');
                  setSuccess('');
                }}
                className="flex-1 py-3 px-4 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors text-sm font-medium"
              >
                Back to Sign In
              </button>
              <button
                type="submit"
                disabled={isLoading}
                className={`flex-1 bg-gradient-to-r ${currentColors.buttonGradient} disabled:from-gray-400 disabled:to-gray-500 text-white font-bold py-3 px-4 rounded-lg transition-all text-sm`}
              >
                {isLoading ? 'Sending...' : 'Send Reset Link'}
              </button>
            </div>
          </form>
        ) : (
          <div className="space-y-4">
            <form onSubmit={handleEmailLogin} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Email Address
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                  style={{ borderColor: 'rgb(209 213 219)' }}
                  className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:border-transparent dark:bg-gray-700 dark:text-white text-sm sm:text-base"
                  onFocus={(e) => e.target.style.borderColor = currentColors.primary}
                  onBlur={(e) => e.target.style.borderColor = 'rgb(209 213 219)'}
                  placeholder="you@company.com"
                  autoComplete="email"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Password
                </label>
                <input
                  type="password"
                  name="password"
                  value={formData.password}
                  onChange={handleInputChange}
                  required
                  style={{ borderColor: 'rgb(209 213 219)' }}
                  className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:border-transparent dark:bg-gray-700 dark:text-white text-sm sm:text-base"
                  onFocus={(e) => e.target.style.borderColor = currentColors.primary}
                  onBlur={(e) => e.target.style.borderColor = 'rgb(209 213 219)'}
                  placeholder="Enter your password"
                  autoComplete="current-password"
                />
                <div className="text-right mt-2">
                  <button
                    type="button"
                    onClick={() => {
                      setShowForgotPassword(true);
                      setError('');
                      setSuccess('');
                    }}
                    className={`text-sm ${currentColors.textColor} ${currentColors.hoverTextColor} font-medium`}
                  >
                    Forgot Password?
                  </button>
                </div>
              </div>

              <button
                type="submit"
                disabled={isLoading}
                className={`w-full bg-gradient-to-r ${currentColors.buttonGradient} disabled:from-gray-400 disabled:to-gray-500 text-white font-bold py-3 px-4 rounded-lg transition-all text-sm sm:text-base`}
              >
                {isLoading ? 'Signing In...' : 'Sign In'}
              </button>
            </form>

            {/* Divider */}
            <div className="relative my-4">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-300 dark:border-gray-600" />
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 bg-white dark:bg-gray-800 text-gray-500 dark:text-gray-400">or continue with</span>
              </div>
            </div>

            {/* Google Sign In Button */}
            <button
              onClick={handleGoogleLogin}
              disabled={isLoading}
              className="w-full flex items-center justify-center py-3 px-4 border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm bg-white dark:bg-gray-700 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
            >
              <svg className="w-5 h-5 mr-3" viewBox="0 0 24 24" fill="currentColor">
                <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
                <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
              </svg>
              {isLoading ? 'Connecting...' : 'Continue with Google'}
            </button>
            
            {/* Provider indicator */}
            {oauthConfig && (
              <p className="text-center text-xs text-gray-400 dark:text-gray-500 mt-2">
                {(() => {
                  const productionDomains = ['adelevate.3stech.ai', 'contentstudiosai.com', 'ad-tech-debug.emergent.host'];
                  const isProductionDomain = productionDomains.includes(window.location.hostname);
                  if (oauthConfig.provider === 'custom' && isProductionDomain) {
                    return `🔐 Secured by ${window.location.hostname}`;
                  }
                  return '🛡️ Secured by Emergent';
                })()}
              </p>
            )}
            
            <div className="text-center mt-4">
              <p className="text-xs text-gray-500 dark:text-gray-400">
                By signing in, you agree to our{' '}
                <a href="#" className={`${currentColors.textColor} hover:underline`}>Terms of Service</a>
                {' '}and{' '}
                <a href="#" className={`${currentColors.textColor} hover:underline`}>Privacy Policy</a>
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default LoginModal;
